/**
 * AI Background Effects Component
 * Efectos visuales AI/Tech para la interfaz
 * Desarrollado por Rafael Martinez
 * © 2025 - Todos los derechos reservados
 */

import React, { useEffect, useRef, useState } from "react";
import "./AIBackgroundEffects.css";

const AIBackgroundEffects = ({ step = 1, withSidebar = false }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const getStepColor = () => {
    switch (step) {
      case 1:
        return { primary: "#0066ff", secondary: "#4d94ff" };
      case 2:
        return { primary: "#ff006e", secondary: "#ff4d94" };
      case 3:
        return { primary: "#10b981", secondary: "#34d399" };
      default:
        return { primary: "#0066ff", secondary: "#4d94ff" };
    }
  };

  const colors = getStepColor();

  return (
    <div className={`ai-effects-container ${withSidebar ? 'with-sidebar' : ''}`}>
      {/* Orbiting particles around the card */}
      <div className="orbiting-particles">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`orbit-particle particle-${i}`}
            style={{
              "--particle-color": colors.primary,
              "--particle-delay": `${i * 0.5}s`,
              "--orbit-duration": `${20 + i * 2}s`,
            }}
          >
            <div className="particle-core"></div>
            <div className="particle-glow"></div>
          </div>
        ))}
      </div>

      {/* Left side neural network */}
      <div className="side-panel left-panel">
        <div className="neural-visualization">
          <svg viewBox="0 0 200 400" className="neural-svg">
            {/* Neural network paths */}
            {[...Array(5)].map((_, i) => (
              <g key={`left-${i}`}>
                <circle
                  cx="50"
                  cy={80 + i * 60}
                  r="8"
                  fill={colors.primary}
                  className="neural-node"
                  style={{ animationDelay: `${i * 0.2}s` }}
                />
                {[...Array(3)].map((_, j) => (
                  <line
                    key={`left-line-${i}-${j}`}
                    x1="50"
                    y1={80 + i * 60}
                    x2="150"
                    y2={100 + j * 100}
                    stroke={colors.secondary}
                    strokeWidth="1"
                    opacity="0.3"
                    className="neural-connection"
                  />
                ))}
              </g>
            ))}
            {[...Array(3)].map((_, i) => (
              <circle
                key={`left-output-${i}`}
                cx="150"
                cy={100 + i * 100}
                r="10"
                fill={colors.secondary}
                className="neural-node output-node"
                style={{ animationDelay: `${0.5 + i * 0.2}s` }}
              />
            ))}
          </svg>

          {/* AI Labels */}
          <div className="ai-labels left-labels">
            <span
              className="ai-label"
              style={{ top: "20%", color: colors.primary }}
            >
              INPUT
            </span>
            <span
              className="ai-label"
              style={{ top: "40%", color: colors.secondary }}
            >
              PROCESS
            </span>
            <span
              className="ai-label"
              style={{ top: "60%", color: colors.primary }}
            >
              LEARN
            </span>
            <span
              className="ai-label"
              style={{ top: "80%", color: colors.secondary }}
            >
              OUTPUT
            </span>
          </div>
        </div>

        {/* Floating code snippets */}
        <div className="code-snippets left-snippets">
          <div className="code-block" style={{ "--block-delay": "0s" }}>
            <span style={{ color: colors.primary }}>const</span> agent ={" "}
            <span style={{ color: colors.secondary }}>new VoiceAI()</span>;
          </div>
          <div className="code-block" style={{ "--block-delay": "2s" }}>
            <span style={{ color: colors.primary }}>agent</span>.
            <span style={{ color: colors.secondary }}>train</span>(data);
          </div>
          <div className="code-block" style={{ "--block-delay": "4s" }}>
            <span style={{ color: colors.primary }}>await</span>{" "}
            <span style={{ color: colors.secondary }}>agent.respond()</span>;
          </div>
        </div>
      </div>

      {/* Right side data flow */}
      <div className="side-panel right-panel">
        <div className="data-flow">
          {/* Binary stream */}
          <div className="binary-stream">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="binary-line"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                {Math.random().toString(2).substr(2, 8)}
              </div>
            ))}
          </div>

          {/* Tech metrics */}
          <div className="tech-metrics">
            <div className="metric-item">
              <span
                className="metric-label"
                style={{ color: colors.secondary }}
              >
                Accuracy
              </span>
              <div className="metric-bar">
                <div
                  className="metric-fill"
                  style={{ width: "95%", background: colors.primary }}
                ></div>
              </div>
              <span className="metric-value">95%</span>
            </div>
            <div className="metric-item">
              <span
                className="metric-label"
                style={{ color: colors.secondary }}
              >
                Processing
              </span>
              <div className="metric-bar">
                <div
                  className="metric-fill animated-fill"
                  style={{ background: colors.primary }}
                ></div>
              </div>
              <span className="metric-value">Active</span>
            </div>
            <div className="metric-item">
              <span
                className="metric-label"
                style={{ color: colors.secondary }}
              >
                Response Time
              </span>
              <div className="metric-bar">
                <div
                  className="metric-fill"
                  style={{ width: "87%", background: colors.primary }}
                ></div>
              </div>
              <span className="metric-value">87ms</span>
            </div>
          </div>

          {/* Waveform animation */}
          <div className="waveform">
            <svg viewBox="0 0 200 100" className="waveform-svg">
              <path
                d="M0,50 Q50,20 100,50 T200,50"
                stroke={colors.primary}
                strokeWidth="2"
                fill="none"
                className="wave-path"
              />
              <path
                d="M0,50 Q50,80 100,50 T200,50"
                stroke={colors.secondary}
                strokeWidth="2"
                fill="none"
                className="wave-path-2"
              />
            </svg>
          </div>
        </div>

        {/* AI Features list */}
        <div className="ai-features">
          <div className="feature-item" style={{ "--feature-delay": "0s" }}>
            <span className="feature-icon" style={{ color: colors.primary }}>
              ◉
            </span>
            <span>Natural Language</span>
          </div>
          <div className="feature-item" style={{ "--feature-delay": "0.5s" }}>
            <span className="feature-icon" style={{ color: colors.secondary }}>
              ◉
            </span>
            <span>Voice Synthesis</span>
          </div>
          <div className="feature-item" style={{ "--feature-delay": "1s" }}>
            <span className="feature-icon" style={{ color: colors.primary }}>
              ◉
            </span>
            <span>Real-time Processing</span>
          </div>
          <div className="feature-item" style={{ "--feature-delay": "1.5s" }}>
            <span className="feature-icon" style={{ color: colors.secondary }}>
              ◉
            </span>
            <span>Machine Learning</span>
          </div>
        </div>
      </div>

      {/* Top floating particles */}
      <div className="top-particles">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`floating-particle fp-${i}`}
            style={{
              "--fp-color": i % 2 === 0 ? colors.primary : colors.secondary,
              left: `${20 + i * 15}%`,
            }}
          >
            <div className="fp-core"></div>
          </div>
        ))}
      </div>

      {/* Bottom tech indicators */}
      <div className="bottom-indicators">
        <div className="indicator-item">
          <span
            className="indicator-dot"
            style={{ background: colors.primary }}
          ></span>
          <span className="indicator-label">AI Ready</span>
        </div>
        <div className="indicator-item">
          <span
            className="indicator-dot pulse"
            style={{ background: colors.secondary }}
          ></span>
          <span className="indicator-label">Processing</span>
        </div>
        <div className="indicator-item">
          <span
            className="indicator-dot"
            style={{ background: "#10b981" }}
          ></span>
          <span className="indicator-label">Connected</span>
        </div>
      </div>

      {/* Interactive glow that follows mouse */}
      <div
        className="mouse-glow"
        style={{
          left: mousePosition.x + "px",
          top: mousePosition.y + "px",
          background: `radial-gradient(circle, ${colors.primary}20, transparent 70%)`,
        }}
      />
    </div>
  );
};

export default AIBackgroundEffects;
